import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Alert,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  FormCard,
  FormTextField,
  FormCheckboxField,
  BackButton,
  NextButton,
} from './UI';
import { CreateDonorRequest, Donor } from '@/types';
import { apiService } from '@/services/api';
import { notificationService } from '@/services/notificationService';

type DonorFormData = {
  firstName: string;
  lastName: string;
  email: string;
  company?: string;
  phone?: string;
  address?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  gdprConsent: boolean;
  marketingConsent?: boolean;
  fiscalCode?: string;
  vatNumber?: string;
  sdiCode?: string;
  pec?: string;
};

interface DonorCreationStepProps {
  initialData?: Partial<CreateDonorRequest>;
  selectedDonor?: Donor;
  onDataChange: (data: CreateDonorRequest) => void;
  onNext: () => void;
  onBack: () => void;
  showNavigationButtons?: boolean;
}

const schema = yup.object({
  firstName: yup.string().required('Il nome è obbligatorio'),
  lastName: yup.string().required('Il cognome è obbligatorio'),
  email: yup.string().email('Email non valida').required('L\'email è obbligatoria'),
  company: yup.string().optional(),
  phone: yup.string().optional(),
  address: yup.string().optional(),
  city: yup.string().optional(),
  postalCode: yup.string().optional(),
  country: yup.string().optional(),
  gdprConsent: yup.boolean().required('Devi accettare il trattamento dei dati'),
  marketingConsent: yup.boolean().optional(),
  fiscalCode: yup.string().optional().test('fiscal-code-format', 'Il codice fiscale deve essere di 16 caratteri alfanumerici', function(value) {
    if (!value || value.trim() === '') return true;
    if (value.length !== 16) return false;
    return /^[A-Z0-9]{16}$/.test(value);
  }),
  vatNumber: yup.string().optional().test('vat-number-format', 'La partita IVA deve essere di 11 cifre', function(value) {
    if (!value || value.trim() === '') return true;
    if (value.length !== 11) return false;
    return /^[0-9]{11}$/.test(value);
  }),
  sdiCode: yup.string().optional().test('sdi-code-format', 'Il codice SDI deve essere di 6-7 caratteri alfanumerici maiuscoli', function(value) {
    if (!value || value.trim() === '') return true;
    if (value.length < 6 || value.length > 7) return false;
    return /^[A-Z0-9]{6,7}$/.test(value);
  }),
  pec: yup.string().email('PEC non valida').optional(),
}).test('fiscal-data-required', 'È obbligatorio inserire il Codice Fiscale o la Partita IVA', function(value) {
  const { fiscalCode, vatNumber } = value;
  const hasFiscalCode = fiscalCode && fiscalCode.trim() !== '';
  const hasVatNumber = vatNumber && vatNumber.trim() !== '';
  
  if (!hasFiscalCode && !hasVatNumber) {
    return this.createError({ message: 'È obbligatorio inserire il Codice Fiscale o la Partita IVA' });
  }
  return true;
});

export const DonorCreationStep: React.FC<DonorCreationStepProps> = ({
  initialData,
  selectedDonor,
  onDataChange,
  onNext,
  onBack,
  showNavigationButtons = true,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);
  const [duplicateError, setDuplicateError] = useState<string | null>(null);
  
  // Se c'è un donatore selezionato, i campi devono essere disabilitati
  const isDonorSelected = !!selectedDonor;

  // Se c'è un donatore selezionato, usa i suoi dati, altrimenti usa initialData
  const formData = selectedDonor ? {
    firstName: selectedDonor.firstName || '',
    lastName: selectedDonor.lastName || '',
    email: selectedDonor.email || '',
    company: selectedDonor.company || '',
    phone: selectedDonor.phone || '',
    address: selectedDonor.address || '',
    city: selectedDonor.city || '',
    postalCode: selectedDonor.postalCode || '',
    country: selectedDonor.country || 'Italia',
    gdprConsent: selectedDonor.gdprConsent || false,
    marketingConsent: selectedDonor.marketingConsent || false,
    fiscalCode: selectedDonor.fiscalCode || '',
    vatNumber: selectedDonor.vatNumber || '',
    sdiCode: selectedDonor.sdiCode || '',
    pec: selectedDonor.pec || '',
  } : {
    firstName: initialData?.firstName || '',
    lastName: initialData?.lastName || '',
    email: initialData?.email || '',
    company: initialData?.company || '',
    phone: initialData?.phone || '',
    address: initialData?.address || '',
    city: initialData?.city || '',
    postalCode: initialData?.postalCode || '',
    country: initialData?.country || 'Italia',
    gdprConsent: initialData?.gdprConsent || false,
    marketingConsent: initialData?.marketingConsent || false,
    fiscalCode: initialData?.fiscalCode || '',
    vatNumber: initialData?.vatNumber || '',
    sdiCode: initialData?.sdiCode || '',
    pec: initialData?.pec || '',
  };

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm<DonorFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: formData.firstName || '',
      lastName: formData.lastName || '',
      email: formData.email || '',
      company: formData.company || '',
      phone: formData.phone || '',
      address: formData.address || '',
      city: formData.city || '',
      postalCode: formData.postalCode || '',
      country: formData.country || 'IT',
      gdprConsent: formData.gdprConsent || false,
      marketingConsent: formData.marketingConsent || false,
      fiscalCode: formData.fiscalCode || '',
      vatNumber: formData.vatNumber || '',
      sdiCode: formData.sdiCode || '',
    },
    mode: 'onChange',
  });

  const watchedValues = watch();

  // Aggiorna i dati quando cambiano
  React.useEffect(() => {
    if (isValid) {
      onDataChange(watchedValues as CreateDonorRequest);
    }
  }, [watchedValues, isValid, onDataChange]);

  const onSubmit = async (data: DonorFormData) => {
    console.log('🔍 Starting duplicate check with data:', data);
    setIsSubmitting(true);
    setIsCheckingDuplicates(true);
    setDuplicateError(null);
    
    try {
      // Pulisci i dati: converte stringhe vuote in undefined per i campi opzionali
      const cleanData: CreateDonorRequest = {
        ...data,
        company: data.company?.trim() ? data.company.trim() : undefined,
        phone: data.phone?.trim() ? data.phone.trim() : undefined,
        address: data.address?.trim() ? data.address.trim() : undefined,
        city: data.city?.trim() ? data.city.trim() : undefined,
        postalCode: data.postalCode?.trim() ? data.postalCode.trim() : undefined,
        country: data.country?.trim() ? data.country.trim() : undefined,
        fiscalCode: data.fiscalCode?.trim() ? data.fiscalCode.trim() : undefined,
        vatNumber: data.vatNumber?.trim() ? data.vatNumber.trim() : undefined,
        sdiCode: data.sdiCode?.trim() ? data.sdiCode.trim() : undefined,
      };

      console.log('🧹 Cleaned data for duplicate check:', {
        email: cleanData.email,
        fiscalCode: cleanData.fiscalCode,
        vatNumber: cleanData.vatNumber
      });

      // Controlla duplicati prima di procedere
      console.log('🔍 Calling checkDonorDuplicates...');
      const duplicateCheck = await apiService.checkDonorDuplicates({
        email: cleanData.email,
        fiscalCode: cleanData.fiscalCode,
        vatNumber: cleanData.vatNumber,
      });

      console.log('📋 Duplicate check result:', duplicateCheck);

      if (duplicateCheck.hasDuplicates) {
        console.log('❌ DUPLICATES FOUND - Blocking navigation');
        const { duplicates } = duplicateCheck;
        let errorMessage = 'Donatore già esistente:\n';
        
        if (duplicates.email) {
          errorMessage += `• Email: ${duplicates.email.firstName} ${duplicates.email.lastName} (${duplicates.email.email})\n`;
        }
        if (duplicates.fiscalCode) {
          errorMessage += `• Codice Fiscale: ${duplicates.fiscalCode.firstName} ${duplicates.fiscalCode.lastName} (${duplicates.fiscalCode.fiscalCode})\n`;
        }
        if (duplicates.vatNumber) {
          errorMessage += `• Partita IVA: ${duplicates.vatNumber.firstName} ${duplicates.vatNumber.lastName} (${duplicates.vatNumber.vatNumber})\n`;
        }
        
        console.log('🚫 Setting duplicate error:', errorMessage.trim());
        setDuplicateError(errorMessage.trim());
        notificationService.error('Duplicato trovato', 'Esiste già un donatore con questi dati');
        return;
      }
      
      console.log('✅ No duplicates found - Proceeding to next step');
      // Nessun duplicato trovato, procedi
      onDataChange(cleanData);
      onNext();
    } catch (error) {
      console.error('❌ Error checking duplicates or saving donor data:', error);
      notificationService.error('Errore', 'Errore durante il controllo duplicati');
    } finally {
      setIsSubmitting(false);
      setIsCheckingDuplicates(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Dati Donatore
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {isDonorSelected 
          ? 'I dati del donatore selezionato sono stati caricati automaticamente. I campi non sono modificabili.'
          : 'Compila i dati del donatore. I campi contrassegnati con * sono obbligatori. È necessario inserire il Codice Fiscale o la Partita IVA.'
        }
      </Typography>

      {isDonorSelected && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Donatore selezionato:</strong> {selectedDonor.firstName} {selectedDonor.lastName} ({selectedDonor.email})
          </Typography>
        </Alert>
      )}

      {duplicateError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
            {duplicateError}
          </Typography>
        </Alert>
      )}

      <FormCard
        title="Informazioni Personali"
        subtitle="Dati anagrafici del donatore"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                  <FormTextField
                    {...field}
                    label="Nome *"
                    error={errors.firstName?.message}
                    required
                    disabled={isDonorSelected}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <FormTextField
                    {...field}
                    label="Cognome *"
                    error={errors.lastName?.message}
                    required
                    disabled={isDonorSelected}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <FormTextField
                    {...field}
                    label="Email *"
                    type="email"
                    error={errors.email?.message}
                    required
                    disabled={isDonorSelected}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <FormTextField
                    {...field}
                    label="Telefono"
                    type="tel"
                    error={errors.phone?.message}
                    value={field.value || ''}
                    disabled={isDonorSelected}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="company"
                control={control}
                render={({ field }) => (
                  <FormTextField
                    {...field}
                    label="Azienda"
                    error={errors.company?.message}
                    value={field.value || ''}
                    disabled={isDonorSelected}
                  />
                )}
              />
            </Grid>
          </Grid>
        </form>
      </FormCard>

      <FormCard
        title="Dati Fiscali"
        subtitle="Informazioni per fatturazione (opzionale)"
        sx={{ mt: 3 }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} sm={4}>
            <Controller
              name="fiscalCode"
              control={control}
              render={({ field }) => (
                <FormTextField
                  {...field}
                  label="Codice Fiscale"
                  error={errors.fiscalCode?.message}
                  value={field.value || ''}
                  disabled={isDonorSelected}
                  onChange={(value) => {
                    const upperValue = value.toUpperCase();
                    field.onChange(upperValue);
                  }}
                  placeholder="RSSMRA80A01H501U"
                  helperText="Esattamente 16 caratteri alfanumerici maiuscoli"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller
              name="vatNumber"
              control={control}
              render={({ field }) => (
                <FormTextField
                  {...field}
                  label="Partita IVA"
                  error={errors.vatNumber?.message}
                  value={field.value || ''}
                  disabled={isDonorSelected}
                  onChange={(value) => {
                    const numericValue = value.replace(/\D/g, ''); // Solo numeri
                    field.onChange(numericValue);
                  }}
                  placeholder="12345678901"
                  helperText="Esattamente 11 cifre numeriche"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller
              name="sdiCode"
              control={control}
              render={({ field }) => (
                <FormTextField
                  {...field}
                  label="Codice SDI"
                  error={errors.sdiCode?.message}
                  value={field.value || ''}
                  disabled={isDonorSelected}
                  onChange={(value) => {
                    const upperValue = value.toUpperCase();
                    field.onChange(upperValue);
                  }}
                  placeholder="ABC123"
                  helperText="6-7 caratteri alfanumerici maiuscoli"
                />
              )}
            />
          </Grid>
        </Grid>
        
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <Controller
              name="pec"
              control={control}
              render={({ field }) => (
                <FormTextField
                  {...field}
                  label="PEC"
                  error={errors.pec?.message}
                  value={field.value || ''}
                  disabled={isDonorSelected}
                  placeholder="azienda@pec.it"
                  helperText="Indirizzo email PEC (opzionale)"
                />
              )}
            />
          </Grid>
        </Grid>
      </FormCard>

      <FormCard
        title="Indirizzo"
        subtitle="Informazioni di residenza (opzionale)"
        sx={{ mt: 3 }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Controller
              name="address"
              control={control}
              render={({ field }) => (
                  <FormTextField
                    {...field}
                    label="Indirizzo"
                    error={errors.address?.message}
                    value={field.value || ''}
                    disabled={isDonorSelected}
                  />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller
              name="city"
              control={control}
              render={({ field }) => (
                  <FormTextField
                    {...field}
                    label="Città"
                    error={errors.city?.message}
                    value={field.value || ''}
                    disabled={isDonorSelected}
                  />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller
              name="postalCode"
              control={control}
              render={({ field }) => (
                  <FormTextField
                    {...field}
                    label="CAP"
                    error={errors.postalCode?.message}
                    value={field.value || ''}
                    disabled={isDonorSelected}
                  />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Controller
              name="country"
              control={control}
              render={({ field }) => (
                  <FormTextField
                    {...field}
                    label="Paese"
                    error={errors.country?.message}
                    value={field.value || ''}
                    disabled={isDonorSelected}
                  />
              )}
            />
          </Grid>
        </Grid>
      </FormCard>

      <FormCard
        title="Consensi"
        subtitle="Autorizzazioni per il trattamento dei dati"
        sx={{ mt: 3 }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Controller
            name="gdprConsent"
            control={control}
            render={({ field }) => (
              <FormCheckboxField
                checked={field.value || false}
                onChange={field.onChange}
                label="Accetto il trattamento dei miei dati personali ai sensi del GDPR *"
                error={errors.gdprConsent?.message}
                disabled={isDonorSelected}
              />
            )}
          />
          
          <Controller
            name="marketingConsent"
            control={control}
            render={({ field }) => (
              <FormCheckboxField
                checked={field.value || false}
                onChange={field.onChange}
                label="Accetto di ricevere comunicazioni promozionali (opzionale)"
                error={errors.marketingConsent?.message}
                disabled={isDonorSelected}
              />
            )}
          />
        </Box>

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            I tuoi dati saranno utilizzati esclusivamente per gestire la donazione e 
            per gli scopi istituzionali della Fondazione, nel rispetto della privacy.
          </Typography>
        </Alert>
      </FormCard>

      {showNavigationButtons && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <BackButton onClick={onBack} />
          <NextButton
            onClick={handleSubmit(onSubmit)}
            disabled={!isValid || isCheckingDuplicates}
            loading={isSubmitting || isCheckingDuplicates}
          />
        </Box>
      )}
    </Box>
  );
};
